import React from "react";
import { useLocation } from "react-router-dom";
import { MobileScreenSettingsScreenTypes } from "@/types/mobile-settings";
import { useMobileSettings } from "./hooks/useMobileSettings";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { CustomizationSettings } from "./components/CustomizationSettings";
import { PhonePreview } from "@/components/common/PhonePreview";
import { ChatDisplayPreview } from "./components/ChatDisplayPreview";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SettingsNavigation } from "./components/Navigation";
import { DashboardSettingsPage } from "./DashboardSettingsPage";
import { useTranslation } from "react-i18next";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isRapidPlan } from "@/utils/accessControl";

export const MobileSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const hotelId = useCurrentHotelId() ?? "";
  const location = useLocation();
  const activeTab = location.pathname.includes("/dashboard")
    ? "dashboard"
    : "customization";

  const { settings, isLoading, updateSettings, isUpdating } = useMobileSettings(
    hotelId,
    MobileScreenSettingsScreenTypes.DURING_STAY,
  );
  const [previewSettings, setPreviewSettings] = React.useState(settings);

  React.useEffect(() => {
    setPreviewSettings(settings);
  }, [settings]);

  if (activeTab === "dashboard") {
    return <DashboardSettingsPage />;
  }

  if (isLoading) return <div className="p-8">{t("common.loading")}</div>;
  if (!settings)
    return (
      <div className="p-8">
        {t("mobileSettings.customization.settingsNotFound")}
      </div>
    );

  return (
    <div className="flex h-full overflow-hidden bg-white">
      {/* Left Column - Settings */}
      <div className="hide-scrollbar flex-3 overflow-y-auto border-r border-zinc-200 p-12">
        <SettingsNavigation activeTab={activeTab} />

        <CustomizationSettings
          settings={settings}
          onUpdate={updateSettings}
          isUpdating={isUpdating}
          onPreviewChange={setPreviewSettings}
          showChatDisplay={!isRapidPlan(userProfile)}
        />
      </div>

      {/* Right Column - Preview */}
      <div className="hide-scrollbar flex flex-2 flex-col items-center gap-8 overflow-y-auto bg-zinc-50/50 p-8">
        <div className="flex w-full items-center gap-4">
          <Select defaultValue="original">
            <SelectTrigger className="flex-1 justify-between">
              <SelectValue
                placeholder={t("mobileSettings.preview.selectVersion")}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="original">
                  {t("mobileSettings.preview.original")}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Button variant="new_outline" className="flex-1">
            {t("mobileSettings.preview.button")}
          </Button>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <h2 className="text-2xl font-bold">
            {t("mobileSettings.preview.title")}
          </h2>

          <PhonePreview
            serviceHeader={false}
            containerClassName="w-[400px] border-[12px] border-zinc-900 rounded-[3rem] shadow-2xl"
          >
            <ChatDisplayPreview settings={previewSettings || settings} />
          </PhonePreview>
        </div>
      </div>
    </div>
  );
};
